import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, Pagination } from "@/components/ui-system";
import { LOG_PAGE_SIZE, logsParams, rangeLabel } from "@/lib/sendHistory";
import { toast } from "sonner";
import {
  Mail, Plus, Trash2, Send, Loader2, Clock, CheckCircle2,
  XCircle, Bell, Settings, History, ChevronDown, ChevronUp, Save, Edit, X, FileText, ArrowLeft,
} from "lucide-react";
import GeneralEmailTemplates, { TEMPLATE_ACTION_TYPES, templatePageTitle } from "./GeneralEmailTemplates";

// Weekly schedule day-of-week options (0=Mon … 6=Sun, matches Python weekday()/IST cron).
const DOW_OPTIONS = [
  { v: 0, l: "Monday" }, { v: 1, l: "Tuesday" }, { v: 2, l: "Wednesday" }, { v: 3, l: "Thursday" },
  { v: 4, l: "Friday" }, { v: 5, l: "Saturday" }, { v: 6, l: "Sunday" },
];

// Trigger Time choices in 30-minute steps. Stored as 24-hour "HH:MM" (what the
// backend parses), shown to the admin as 12-hour.
const CUTOFF_TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 ? "30" : "00";
  return {
    value: `${String(h).padStart(2, "0")}:${m}`,
    label: `${h % 12 === 0 ? 12 : h % 12}:${m} ${h >= 12 ? "PM" : "AM"}`,
  };
});

/**
 * Trigger Time picker — shared by the Add Notification form and the saved card so
 * both offer the same half-hour steps. A config saved before the dropdown existed
 * can hold an off-step time; it is listed too so the control never renders blank.
 */
function TriggerTimeSelect({ id, value, onChange, className = "" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className={className} data-testid={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {value && !CUTOFF_TIME_OPTIONS.some(o => o.value === value) && (
          <SelectItem value={value}>{value}</SelectItem>
        )}
        {CUTOFF_TIME_OPTIONS.map(o => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const NOTIFICATION_TYPE_META = {
  invoice_generation_reminder: {
    icon: Clock,
    color: "bg-red-100 text-red-700",
    description: "Daily reminder when completed months have no invoice generated for sellers.",
  },
  new_seller_onboarding: {
    icon: Bell,
    color: "bg-blue-100 text-blue-700",
    description: "When a new seller is registered in the system.",
  },
  new_order_creation: {
    icon: Mail,
    color: "bg-green-100 text-green-700",
    description: "When a new order is created by any seller.",
  },
  order_status_update: {
    icon: Bell,
    color: "bg-amber-100 text-amber-700",
    description: "When an order status changes.",
  },
  order_pod: {
    icon: CheckCircle2,
    color: "bg-emerald-100 text-emerald-700",
    description: "When an order is marked as delivered.",
  },
  pickup_request_creation: {
    icon: Bell,
    color: "bg-purple-100 text-purple-700",
    description: "When a pickup request is created.",
  },
  pickup_status_digest: {
    icon: CheckCircle2,
    color: "bg-teal-100 text-teal-700",
    description: "Daily digest of scheduled pickups with one-click Out for Pickup / Picked Up / Not Picked actions (one status per pickup).",
  },
  appointment_tat_breach: {
    icon: Clock,
    color: "bg-orange-100 text-orange-700",
    description: "When appointment time has passed but order is not delivered.",
  },
  low_balance_alert: {
    icon: XCircle,
    color: "bg-red-100 text-red-700",
    description: "When a seller's wallet balance is below threshold.",
  },
  unknown_freight_field: {
    icon: Bell,
    color: "bg-rose-100 text-rose-700",
    description: "When the shipping partner sends a new freight charge field we don't yet bill (e.g. demurrage_v2).",
  },
  pickup_for_today: {
    icon: Send,
    color: "bg-teal-100 text-teal-700",
  },
};

const EXTERNAL_ACTION_TYPE = "tomorrow_appointment_notification_external";
const PICKUP_TODAY_ACTION_TYPE = "pickup_for_today";

// To addresses allowed for Pickup For Today. Mirrors TRUSTED_TO_DOMAINS in
// backend/services/pickup_for_today_service.py, which enforces it on save and on
// send — this only gives instant feedback.
const TRUSTED_TO_DOMAINS = ["delhivery.com", "edgistify.com"];

// Error message if `email` can't be a To address for this notification, else null.
const toDomainError = (actionType, field, email) => {
  if (actionType !== PICKUP_TODAY_ACTION_TYPE || field !== "recipient_emails") return null;
  const domain = email.split("@").pop().toLowerCase();
  return TRUSTED_TO_DOMAINS.includes(domain)
    ? null
    : `To must be an ${TRUSTED_TO_DOMAINS.map((d) => `@${d}`).join(" or ")} address`;
};

/** Action types whose service reads recipient_names for the email greeting. */
const GREETING_TYPES = [EXTERNAL_ACTION_TYPE, PICKUP_TODAY_ACTION_TYPE];

/**
 * Types whose greeting also names the CC recipients. Pickup For Today addresses the
 * carrier's shared pickup mailbox in TO and the people by name in CC, so the name
 * inputs have to cover CC as well or those names can never be set.
 */
const GREETING_INCLUDES_CC = [PICKUP_TODAY_ACTION_TYPE];

/**
 * Editable list of IST schedule times.
 *
 * Edited and stored as 24-hour {hour, minute} — the same format the single-time
 * notification types use and the same the scheduler reads, so there is one time
 * format on the page and nothing to convert on save.
 */
function ScheduleTimes({ times, onChange }) {
  const list = Array.isArray(times) ? times : [];
  const setAt = (i, next) => onChange(list.map((t, j) => (j === i ? next : t)));

  return (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule times (IST)</label>
      <p className="text-xs text-gray-400 mt-0.5">Each time is an independent send. A missed time is still sent until the next one is due.</p>
      {list.length > 0 && (
        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 mt-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Hour (0-23, IST)</span>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Minute (0-59)</span>
          <span className="w-9" />
        </div>
      )}
      <div className="space-y-2 mt-1">
        {list.map((t, i) => {
          const h = Number.isInteger(t?.hour) ? t.hour : 9;
          const m = Number.isInteger(t?.minute) ? t.minute : 0;
          return (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
              <Input type="number" min={0} max={23} className="text-sm"
                value={h}
                onChange={e => {
                  const v = e.target.value === "" ? 0 : parseInt(e.target.value);
                  if (isNaN(v) || v < 0 || v > 23) return;
                  setAt(i, { hour: v, minute: m });
                }} />
              <Input type="number" min={0} max={59} className="text-sm"
                value={String(m).padStart(2, "0")}
                onChange={e => {
                  const v = e.target.value === "" ? 0 : parseInt(e.target.value);
                  if (isNaN(v) || v < 0 || v > 59) return;
                  setAt(i, { hour: h, minute: v });
                }} />
              <Button size="sm" variant="ghost" className="h-8 text-red-500 hover:text-red-700"
                onClick={() => onChange(list.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
      </div>
      <Button size="sm" variant="outline" className="mt-2 h-8"
        onClick={() => onChange([...list, { hour: 9, minute: 0 }])}>
        <Plus className="w-3 h-3 mr-1" /> Add time
      </Button>
      {list.length === 0 && (
        <p className="text-xs text-amber-600 mt-1">No times configured — this notification will never send.</p>
      )}
    </div>
  );
}

/**
 * Optional display names for the TO recipients, used to build the greeting
 * ("Hi Himesh and Rahul,"). Left blank, the backend looks the name up in the users
 * collection and otherwise falls back to "Team" — it never guesses a name from the
 * email address.
 */
function RecipientNames({ emails, names, onChange }) {
  if (!emails || emails.length === 0) return null;
  const map = names || {};
  return (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient names (optional)</label>
      <p className="text-xs text-gray-400 mt-0.5">Used for the email greeting. Blank falls back to the EdgeLMS user name, then to &quot;Team&quot;.</p>
      <div className="space-y-1.5 mt-2">
        {emails.map(em => (
          <div key={em} className="flex items-center gap-2">
            <span className="text-xs text-gray-600 flex-1 truncate" title={em}>{em}</span>
            <Input className="h-8 text-sm w-40" placeholder="Name"
              value={map[em] || ""}
              onChange={e => onChange({ ...map, [em]: e.target.value })} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Which document copies the external appointment digest carries. Each flag controls BOTH
 * the fetch and the column: off means the PDF is never downloaded and the column is
 * absent from the email, not blank.
 *
 * `!== false` rather than a truthy check — a config saved before these flags existed has
 * neither key, and the backend reads that as on. The two must agree or the switch shows
 * off while the email still attaches.
 *
 * Module level, not nested inside the editor: a component defined in a render body is a
 * new type every render and remounts its children (see EmailList below).
 */
function DocumentToggles({ config, onChange }) {
  const rows = [
    { field: "include_invoice_copy", label: "Invoice Copy",
      hint: "Attach the invoice PDF and show the Invoice Copy column." },
    { field: "include_lrn_copy", label: "LRN Copy",
      hint: "Attach the LR copy PDF and show the LRN Copy column." },
  ];
  return (
    <div className="border-t border-gray-100 pt-3">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Document copies</label>
      <p className="text-xs text-gray-400 mt-0.5">
        Applies to scheduled sends and Trigger Now, which read the saved setting — save before triggering.
      </p>
      <div className="space-y-2.5 mt-2">
        {rows.map(r => (
          <div key={r.field} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="text-sm font-medium">{r.label}</span>
              <p className="text-xs text-gray-400">{r.hint}</p>
            </div>
            <Switch
              checked={config[r.field] !== false}
              onCheckedChange={v => onChange(r.field, v)}
              data-testid={`toggle-${r.field}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const AdminNotificationSetup = ({ user, onLogout }) => {
  const [actions, setActions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  // Set by Send history "View logs": { notification_type, announcement_type, label, from, to }; null = all logs.
  const [logFilter, setLogFilter] = useState(null);
  const [ackInstances, setAckInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  // General Email Templates view inside the Configs tab: null = closed,
  // "picker" = template list, otherwise the id of the open template.
  const [templateView, setTemplateView] = useState(null);
  const [triggering, setTriggering] = useState({});
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState("configs");
  const [expandedConfig, setExpandedConfig] = useState(null);
  // Only the external digest confirms in-page; null means no dialog is open.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [newConfig, setNewConfig] = useState({
    action_type: "",
    is_enabled: true,
    recipient_emails: [],
    cc_emails: [],
    bcc_emails: [],
    schedule_enabled: true,
    schedule_frequency: "daily",
    schedule_hour: 9,
    schedule_minute: 30,
    schedule_time: "09:00",
    subject_template: "",
    from_name: "EdgeOS Support Team",
    footer_text: "Regards\nEdgeOS Support Team",
    include_invoice_copy: true,
    include_lrn_copy: true,
    ack_required: false,
    ack_user_emails: [],
    ack_mode: "all",
    ack_reminder_interval_hours: 24,
    ack_max_reminders: 5,
  });
  const [newEmail, setNewEmail] = useState("");
  const [newCcEmail, setNewCcEmail] = useState("");
  const [newBccEmail, setNewBccEmail] = useState("");
  const [newAckEmail, setNewAckEmail] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [actionsRes, configsRes] = await Promise.all([
        axios.get(`${API}/notification-config/admin/actions`),
        axios.get(`${API}/notification-config/admin`),
      ]);
      setActions(actionsRes.data.actions || []);
      setConfigs(configsRes.data.configs || []);
    } catch (err) {
      toast.error("Failed to fetch notification settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notification-config/admin/logs`, { params: logsParams(logPage, logFilter) });
      setLogs(res.data.logs || []);
      setLogTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to fetch logs");
    }
  }, [logPage, logFilter]);

  const viewTemplateLogs = useCallback((filter) => {
    setLogFilter(filter);
    setLogPage(1);
    setActiveTab("logs");
  }, []);

  const fetchAcks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notification-config/admin/ack-instances?limit=100`);
      setAckInstances(res.data.instances || []);
    } catch {
      toast.error("Failed to fetch acknowledgements");
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === "logs") fetchLogs(); }, [activeTab, fetchLogs]);
  useEffect(() => { if (activeTab === "acks") fetchAcks(); }, [activeTab, fetchAcks]);

  const addEmail = (field, inputValue, setInput) => {
    const email = inputValue.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email"); return;
    }
    const domainError = toDomainError(newConfig.action_type, field, email);
    if (domainError) { toast.error(domainError); return; }
    if (newConfig[field].includes(email)) { toast.error("Already added"); return; }
    setNewConfig(p => ({ ...p, [field]: [...p[field], email] }));
    setInput("");
  };

  const removeEmail = (field, email) => {
    setNewConfig(p => ({ ...p, [field]: p[field].filter(e => e !== email) }));
  };

  const handleCreate = async () => {
    if (!newConfig.action_type) { toast.error("Select a notification type"); return; }
    if (!newConfig.recipient_emails.length) { toast.error("Add at least one recipient"); return; }
    if (newConfig.ack_required && !newConfig.ack_user_emails.length) { toast.error("Add at least one acknowledger, or turn off 'Require acknowledgement'"); return; }

    setSaving(p => ({ ...p, create: true }));
    try {
      await axios.post(`${API}/notification-config/admin`, newConfig);
      toast.success("Configuration created");
      setShowAddForm(false);
      setNewConfig({
        action_type: "", is_enabled: true, recipient_emails: [], cc_emails: [], bcc_emails: [],
        schedule_enabled: true, schedule_frequency: "daily", schedule_hour: 9, schedule_minute: 30,
        schedule_time: "09:00", subject_template: "", from_name: "EdgeOS Support Team",
        footer_text: "Regards\nEdgeOS Support Team",
        ack_required: false, ack_user_emails: [], ack_mode: "all", ack_reminder_interval_hours: 24, ack_max_reminders: 5,
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(p => ({ ...p, create: false }));
    }
  };

  const handleUpdate = async (config) => {
    setSaving(p => ({ ...p, [config.action_type]: true }));
    try {
      await axios.put(`${API}/notification-config/admin/${config.action_type}`, config);
      toast.success("Configuration updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(p => ({ ...p, [config.action_type]: false }));
    }
  };

  // The request itself, shared by both paths so the endpoint, toast and refresh
  // stay identical for every notification type.
  const runDelete = async (actionType) => {
    try {
      await axios.delete(`${API}/notification-config/admin/${actionType}`);
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDelete = async (actionType) => {
    // The external digest confirms in-page. A native confirm() is suppressed by
    // some embedded browsers, which hand the page back `false` — the delete then
    // silently never fires. Every other type keeps the original native prompt.
    if (actionType === EXTERNAL_ACTION_TYPE) {
      setDeleteTarget(actionType);
      return;
    }
    if (!confirm("Delete this notification configuration?")) return;
    await runDelete(actionType);
  };

  const confirmExternalDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await runDelete(deleteTarget);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleTrigger = async (actionType) => {
    setTriggering(p => ({ ...p, [actionType]: true }));
    try {
      const res = await axios.post(`${API}/notification-config/admin/trigger/${actionType}`);
      if (res.data.success) {
        toast.success(res.data.message || "Notification sent!");
      } else {
        toast.error(res.data.error || res.data.message || "Failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggering(p => ({ ...p, [actionType]: false }));
    }
  };

  // General Email Templates manage their own config docs (recipients + per-send
  // form), so those are not shown as generic cards here.
  const listedConfigs = configs.filter(c => !TEMPLATE_ACTION_TYPES.includes(c.action_type));
  // An open template names the page; otherwise the generic heading.
  const pageTitle = (activeTab === "configs" && templatePageTitle(templateView)) || "Email Notifications";

  const getActionLabel = (val) => actions.find(a => a.value === val)?.label || val;
  const getActionMeta = (val) => NOTIFICATION_TYPE_META[val] || { icon: Bell, color: "bg-gray-100 text-gray-700", description: "" };
  const supportsSchedule = (val) => actions.find(a => a.value === val)?.supports_schedule || false;
  const scheduleMode = (val) => actions.find(a => a.value === val)?.schedule_mode;
  const supportsTrigger = (val) => ["invoice_generation_reminder", "b2c_cod_reco_reminder", "tomorrow_appointment_notification_external", PICKUP_TODAY_ACTION_TYPE, "pickup_status_digest"].includes(val);
  // Shown as "Trigger Time". It bounds the DATA the email reports on (pickups set to
  // Not picked up to this IST time), not when it sends — sending is still the
  // Schedule Hour/Minute (or Trigger Now).
  const supportsCutoff = (val) => actions.find(a => a.value === val)?.supports_cutoff || false;
  const supportsDocumentToggles = (val) => actions.find(a => a.value === val)?.supports_document_toggles || false;
  const defaultCutoff = (val) => actions.find(a => a.value === val)?.default_cutoff_time || "18:00";

  const updateConfigField = (actionType, field, value) => {
    setConfigs(prev => prev.map(c => c.action_type === actionType ? { ...c, [field]: value } : c));
  };

  const addConfigEmail = (actionType, field, email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Invalid email"); return false; }
    const domainError = toDomainError(actionType, field, email);
    if (domainError) { toast.error(domainError); return false; }
    setConfigs(prev => prev.map(c => {
      if (c.action_type !== actionType) return c;
      const list = c[field] || [];
      if (list.includes(email)) { toast.error("Already added"); return c; }
      return { ...c, [field]: [...list, email] };
    }));
    return true;
  };

  const removeConfigEmail = (actionType, field, email) => {
    setConfigs(prev => prev.map(c =>
      c.action_type === actionType ? { ...c, [field]: (c[field] || []).filter(e => e !== email) } : c
    ));
  };

  const renderEmailSection = (label, emails, field, inputValue, setInput, addFn, removeFn, color = "teal") => (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2 mt-1">
        <Input
          type="email" placeholder={`Add ${label.toLowerCase()} email`} value={inputValue}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFn(); } }}
          className="text-sm h-8"
        />
        <Button size="sm" variant="outline" onClick={addFn} className="h-8 px-2"><Plus className="w-3 h-3" /></Button>
      </div>
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {emails.map((em, i) => (
            <Badge key={i} variant="secondary" className={`bg-${color}-50 text-${color}-700 text-xs`}>
              <Mail className="w-3 h-3 mr-1" />{em}
              <button onClick={() => removeFn(em)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-notification-setup">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            {activeTab === "configs" && templateView && (
              <Button type="button" size="sm"
                onClick={() => setTemplateView(templateView === "picker" ? null : "picker")}
                className="mb-3 bg-teal-600 hover:bg-teal-700 text-white" data-testid="general-templates-back">
                <ArrowLeft className="w-4 h-4 mr-1" />{templateView === "picker" ? "Back to Configs" : "All templates"}
              </Button>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="w-7 h-7 sm:w-8 sm:h-8 text-teal-600 shrink-0" />
              {pageTitle}
            </h1>
            <p className="text-gray-600 mt-1">Configure and trigger email notifications</p>
          </div>
          {/* Scrolls sideways on narrow phones instead of pushing the page wider. */}
          <div className="flex gap-3 max-w-full overflow-x-auto">
            <div className="flex bg-gray-100 rounded-lg p-1 whitespace-nowrap">
              <button
                onClick={() => setActiveTab("configs")}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${activeTab === "configs" ? "bg-white shadow text-teal-700 font-medium" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="tab-configs"
              >
                <Settings className="w-4 h-4 inline mr-1" />Configs
              </button>
              <button
                onClick={() => setActiveTab("logs")}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${activeTab === "logs" ? "bg-white shadow text-teal-700 font-medium" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="tab-logs"
              >
                <History className="w-4 h-4 inline mr-1" />Logs
              </button>
              <button
                onClick={() => setActiveTab("acks")}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${activeTab === "acks" ? "bg-white shadow text-teal-700 font-medium" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="tab-acks"
              >
                <CheckCircle2 className="w-4 h-4 inline mr-1" />Acknowledgements
              </button>
            </div>
          </div>
        </div>

        {activeTab === "configs" && templateView && (
          <GeneralEmailTemplates
            selected={templateView === "picker" ? null : templateView}
            onSelect={(id) => setTemplateView(id || "picker")}
            onClose={() => setTemplateView(null)} onViewLogs={viewTemplateLogs}
            user={user} configs={configs} loading={loading} onConfigSaved={fetchData}
          />
        )}

        {activeTab === "configs" && !templateView && (
          <div className="space-y-6">
            {/* Add New Config */}
            {!showAddForm ? (
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => setShowAddForm(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-config-btn">
                  <Plus className="w-4 h-4 mr-2" />Add Notification
                </Button>
                <Button variant="outline" onClick={() => setTemplateView("picker")}
                  className="text-teal-700 border-teal-200 hover:bg-teal-50" data-testid="general-templates-btn">
                  <FileText className="w-4 h-4 mr-2" />General Email Templates
                </Button>
              </div>
            ) : (
              <Card className="border-teal-200 bg-teal-50/30">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">New Notification Configuration</CardTitle>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}><X className="w-4 h-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Notification Type</label>
                    <Select value={newConfig.action_type} onValueChange={v => {
                      const action = actions.find(a => a.value === v);
                      setNewConfig(p => ({
                        ...p, action_type: v,
                        subject_template: action?.default_subject || p.subject_template,
                        schedule_frequency: action?.schedule_mode || p.schedule_frequency,
                        schedule_day_of_week: action?.schedule_mode === "weekly" ? (p.schedule_day_of_week ?? 5) : null,
                        cutoff_time: action?.supports_cutoff ? (action.default_cutoff_time || "18:00") : null,
                        // The three agreed IST defaults; the admin can edit or add to them.
                        schedule_times: action?.schedule_mode === "multi_daily"
                          ? (p.schedule_times?.length ? p.schedule_times
                             : [{ hour: 6, minute: 0 }, { hour: 9, minute: 0 }, { hour: 11, minute: 30 }])
                          : null,
                      }));
                    }}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {actions.map(a => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Enabled</span>
                    <Switch checked={newConfig.is_enabled} onCheckedChange={v => setNewConfig(p => ({ ...p, is_enabled: v }))} />
                  </div>

                  {renderEmailSection("To", newConfig.recipient_emails, "recipient_emails", newEmail, setNewEmail,
                    () => addEmail("recipient_emails", newEmail, setNewEmail),
                    (em) => removeEmail("recipient_emails", em)
                  )}
                  {renderEmailSection("CC", newConfig.cc_emails, "cc_emails", newCcEmail, setNewCcEmail,
                    () => addEmail("cc_emails", newCcEmail, setNewCcEmail),
                    (em) => removeEmail("cc_emails", em), "blue"
                  )}
                  {renderEmailSection("BCC", newConfig.bcc_emails, "bcc_emails", newBccEmail, setNewBccEmail,
                    () => addEmail("bcc_emails", newBccEmail, setNewBccEmail),
                    (em) => removeEmail("bcc_emails", em), "gray"
                  )}

                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Subject Template</label>
                    <Input className="mt-1 text-sm" value={newConfig.subject_template}
                      onChange={e => setNewConfig(p => ({ ...p, subject_template: e.target.value }))}
                      placeholder="e.g. [Int] {{count}} Reminder: {{date}}"
                    />
                    <p className="text-xs text-gray-400 mt-1">Placeholders (vary by type): {"{{count}}"}, {"{{date}}"}, {"{{field}}"}</p>
                  </div>

                  {GREETING_TYPES.includes(newConfig.action_type) && (
                    <RecipientNames emails={newConfig.recipient_emails}
                      names={newConfig.recipient_names}
                      onChange={v => setNewConfig(p => ({ ...p, recipient_names: v }))} />
                  )}

                  {supportsSchedule(newConfig.action_type) && scheduleMode(newConfig.action_type) === "multi_daily" && (
                    <ScheduleTimes times={newConfig.schedule_times}
                      onChange={v => setNewConfig(p => ({ ...p, schedule_times: v }))} />
                  )}

                  {supportsDocumentToggles(newConfig.action_type) && (
                    <DocumentToggles config={newConfig}
                      onChange={(f, v) => setNewConfig(p => ({ ...p, [f]: v }))} />
                  )}

                  {supportsSchedule(newConfig.action_type) && scheduleMode(newConfig.action_type) !== "multi_daily" && (
                    <div className="space-y-3">
                      {scheduleMode(newConfig.action_type) === "weekly" && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Day of week (IST)</label>
                          <Select value={String(newConfig.schedule_day_of_week ?? 5)}
                            onValueChange={v => setNewConfig(p => ({ ...p, schedule_day_of_week: parseInt(v), schedule_frequency: "weekly" }))}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{DOW_OPTIONS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Hour (0-23, IST)</label>
                          <Input type="number" min={0} max={23} className="mt-1 text-sm"
                            value={newConfig.schedule_hour ?? ""} onChange={e => setNewConfig(p => ({ ...p, schedule_hour: e.target.value ? parseInt(e.target.value) : null }))}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Minute (0-59)</label>
                          <Input type="number" min={0} max={59} className="mt-1 text-sm"
                            value={newConfig.schedule_minute ?? ""} onChange={e => setNewConfig(p => ({ ...p, schedule_minute: e.target.value ? parseInt(e.target.value) : null }))}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {supportsCutoff(newConfig.action_type) && (
                    <div>
                      <label htmlFor="cutoff-new" className="text-xs font-medium text-gray-500 uppercase tracking-wider">Trigger Time (IST)</label>
                      <TriggerTimeSelect id="cutoff-new"
                        value={newConfig.cutoff_time || defaultCutoff(newConfig.action_type)}
                        onChange={v => setNewConfig(p => ({ ...p, cutoff_time: v }))}
                        className="mt-1 w-full sm:w-40 text-sm" />
                      <p className="text-xs text-gray-400 mt-1">
                        Only pickups set to Not picked by this time today are included. Trigger Now appears on the card once the configuration is created.
                      </p>
                    </div>
                  )}

                  {/* Acknowledgement + escalating reminders */}
                  <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Require acknowledgement</span>
                        <p className="text-xs text-gray-400">Selected recipients must acknowledge; reminders escalate until they do.</p>
                      </div>
                      <Switch checked={newConfig.ack_required} onCheckedChange={v => setNewConfig(p => ({ ...p, ack_required: v }))} />
                    </div>
                    {newConfig.ack_required && (
                      <div className="space-y-3 mt-3">
                        {renderEmailSection("Acknowledgers (must also be recipients)", newConfig.ack_user_emails, "ack_user_emails", newAckEmail, setNewAckEmail,
                          () => addEmail("ack_user_emails", newAckEmail, setNewAckEmail),
                          (em) => removeEmail("ack_user_emails", em), "teal")}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Who must ack</label>
                            <select className="mt-1 w-full text-sm border border-gray-200 rounded-md p-2 bg-white"
                              value={newConfig.ack_mode} onChange={e => setNewConfig(p => ({ ...p, ack_mode: e.target.value }))}>
                              <option value="all">All of them</option>
                              <option value="any">Any one</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reminder every (hrs)</label>
                            <Input type="number" min={1} className="mt-1 text-sm" value={newConfig.ack_reminder_interval_hours ?? ""}
                              onChange={e => setNewConfig(p => ({ ...p, ack_reminder_interval_hours: e.target.value ? parseFloat(e.target.value) : null }))} />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Max reminders</label>
                            <Input type="number" min={1} className="mt-1 text-sm" value={newConfig.ack_max_reminders ?? ""}
                              onChange={e => setNewConfig(p => ({ ...p, ack_max_reminders: e.target.value ? parseInt(e.target.value) : null }))} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
                    <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleCreate} disabled={saving.create}>
                      {saving.create ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Create
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Existing Configs */}
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto" /></div>
            ) : listedConfigs.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No notification configurations yet</p>
              </CardContent></Card>
            ) : (
              listedConfigs.map(config => {
                const meta = getActionMeta(config.action_type);
                const Icon = meta.icon;
                const isExpanded = expandedConfig === config.action_type;

                return (
                  <Card key={config.action_type} className="hover:shadow-md transition-shadow" data-testid={`config-${config.action_type}`}>
                    <CardHeader className="pb-2">
                      {/* Wraps on narrow screens so Trigger Time / Trigger Now drop below
                          the title instead of being pushed off the card. */}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3 grow basis-64 min-w-0">
                          <div className={`p-2 rounded-lg ${meta.color}`}><Icon className="w-5 h-5" /></div>
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              {getActionLabel(config.action_type)}
                              <Badge variant={config.is_enabled ? "default" : "secondary"} className={config.is_enabled ? "bg-green-500 text-xs" : "text-xs"}>
                                {config.is_enabled ? "Active" : "Disabled"}
                              </Badge>
                            </CardTitle>
                            <p className="text-sm text-gray-500 mt-0.5">{meta.description}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 m-2">
                          {supportsCutoff(config.action_type) && (
                            <div className="flex items-center gap-2 mr-2">
                              <label
                                htmlFor={`cutoff-${config.action_type}`}
                                className="text-sm font-medium text-gray-500 whitespace-nowrap"
                              >
                                Cut-off Time
                              </label>
                              <TriggerTimeSelect
                                id={`cutoff-${config.action_type}`}
                                value={config.cutoff_time || defaultCutoff(config.action_type)}
                                // Saved on change, like the Active switch beside it: the
                                // Trigger Now button reads the STORED cut-off, so leaving
                                // it unsaved would send yesterday's window.
                                onChange={v => {
                                  updateConfigField(config.action_type, "cutoff_time", v);
                                  handleUpdate({ ...config, cutoff_time: v });
                                }}
                                className="h-8 w-28 text-sm"
                              />
                            </div>
                          )}
                          {supportsTrigger(config.action_type) && (
                            <Button size="sm" variant="outline"
                              onClick={() => handleTrigger(config.action_type)}
                              disabled={triggering[config.action_type]}
                              className="text-teal-600 border-teal-200 hover:bg-teal-50"
                              data-testid={`trigger-${config.action_type}`}
                            >
                              {triggering[config.action_type] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                              Trigger Now
                            </Button>
                          )}
                          <Switch checked={config.is_enabled}
                            onCheckedChange={v => {
                              updateConfigField(config.action_type, "is_enabled", v);
                              handleUpdate({ ...config, is_enabled: v });
                            }}
                          />
                          <Button size="sm" variant="ghost"
                            onClick={() => setExpandedConfig(isExpanded ? null : config.action_type)}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(config.action_type)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      {/* Compact: recipient summary */}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(config.recipient_emails || []).map((em, i) => (
                          <Badge key={i} variant="secondary" className="bg-teal-50 text-teal-700 text-xs">
                            <Mail className="w-3 h-3 mr-1" />{em}
                          </Badge>
                        ))}
                        {(config.cc_emails || []).length > 0 && (
                          <Badge variant="secondary" className="bg-blue-50 text-blue-600 text-xs">CC: {config.cc_emails.length}</Badge>
                        )}
                        {(config.bcc_emails || []).length > 0 && (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">BCC: {config.bcc_emails.length}</Badge>
                        )}
                      </div>

                      {/* Expanded: full edit form */}
                      {isExpanded && (
                        <ExpandedConfigEditor
                          config={config}
                          onUpdate={handleUpdate}
                          saving={saving[config.action_type]}
                          updateField={(f, v) => updateConfigField(config.action_type, f, v)}
                          addEmail={(field, email) => addConfigEmail(config.action_type, field, email)}
                          removeEmail={(field, email) => removeConfigEmail(config.action_type, field, email)}
                          supportsSchedule={supportsSchedule(config.action_type)}
                          scheduleMode={scheduleMode(config.action_type)}
                          supportsDocumentToggles={supportsDocumentToggles(config.action_type)}
                        />
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {activeTab === "logs" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-700">Notification Logs</h2>
              <Button size="sm" variant="outline" onClick={fetchLogs}><History className="w-4 h-4 mr-1" />Refresh</Button>
            </div>
            {logFilter && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm"
                data-testid="logs-filter">
                <span className="text-teal-800 min-w-0 break-words">
                  Showing <span className="font-semibold">{logFilter.label}</span> · {rangeLabel(logFilter)}
                </span>
                <Button type="button" size="sm" variant="outline" onClick={() => { setLogFilter(null); setLogPage(1); }}>
                  <X className="w-4 h-4 mr-1" />Show all logs
                </Button>
              </div>
            )}
            {logs.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-gray-500">{logFilter ? "No sends match this filter" : "No logs yet"}</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {logs.map((log, i) => (
                  <Card key={i} className="hover:shadow-sm transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {log.success ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{log.subject || log.notification_type}</p>
                            <p className="text-xs text-gray-500">
                              {log.sent_at ? new Date(log.sent_at).toLocaleString("en-IN") : "-"} · {log.triggered_by || "system"} · {log.recipient_count || 0} recipients
                              {log.pending_count ? ` · ${log.pending_count} pending` : ""}
                            </p>
                          </div>
                        </div>
                        <Badge variant={log.success ? "default" : "destructive"} className={log.success ? "bg-green-100 text-green-700" : ""}>
                          {log.success ? "Sent" : "Failed"}
                        </Badge>
                      </div>
                      {log.error && <p className="text-xs text-red-500 mt-1 ml-8">{log.error}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <Pagination page={logPage} pages={Math.max(1, Math.ceil(logTotal / LOG_PAGE_SIZE))} total={logTotal}
              pageSize={LOG_PAGE_SIZE} onPageChange={setLogPage} />
          </div>
        )}

        {activeTab === "acks" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-700">Acknowledgement Tracking</h2>
              <Button size="sm" variant="outline" onClick={fetchAcks}><History className="w-4 h-4 mr-1" />Refresh</Button>
            </div>
            {ackInstances.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-gray-500">No acknowledgement-required notifications sent yet</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {ackInstances.map((inst) => {
                  const acks = inst.required_acks || [];
                  const done = acks.filter(a => a.acknowledged).length;
                  const statusColor = inst.status === "acknowledged" ? "bg-green-100 text-green-700"
                    : inst.status === "escalated" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700";
                  return (
                    <Card key={inst.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <p className="font-medium text-sm">{inst.base_subject}</p>
                            <p className="text-xs text-gray-500">
                              {inst.action_type} · sent {inst.created_at ? new Date(inst.created_at).toLocaleString("en-IN") : "-"}
                              {" · "}{inst.reminder_count > 0 ? `${inst.reminder_count} reminder(s)` : "no reminders yet"}
                              {" · "}must ack: {inst.ack_mode === "any" ? "any one" : "all"}
                            </p>
                          </div>
                          <Badge className={statusColor}>{inst.status} · {done}/{acks.length} acked</Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {acks.map((a, idx) => (
                            <span key={idx}
                              className={`text-xs px-2 py-0.5 rounded ${a.acknowledged ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}
                              title={a.acknowledged_at ? `Acknowledged ${new Date(a.acknowledged_at).toLocaleString("en-IN")}` : "Pending"}>
                              {a.acknowledged ? "✓ " : "○ "}{a.email}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={deleteTarget === EXTERNAL_ACTION_TYPE}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete Tomorrow Appointment Notification External?"
        description="This permanently removes the Tomorrow Appointment Notification External configuration — its recipients, schedule times, subject and footer. Its scheduled sends stop immediately. This cannot be undone."
        confirmLabel="Delete Notification"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmExternalDelete}
      />
    </AdminLayout>
  );
};

// Extracted expanded editor component
const ExpandedConfigEditor = ({ config, onUpdate, saving, updateField, addEmail, removeEmail, supportsSchedule, scheduleMode, supportsDocumentToggles }) => {
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const EmailList = ({ label, field, emails, input, setInput, color = "teal" }) => (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      <div className="flex gap-2 mt-1">
        <Input type="email" placeholder={`Add ${label.toLowerCase()}`} value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (addEmail(field, input.trim())) setInput(""); } }}
          className="text-sm h-8"
        />
        <Button size="sm" variant="outline" className="h-8 px-2"
          onClick={() => { if (addEmail(field, input.trim())) setInput(""); }}
        ><Plus className="w-3 h-3" /></Button>
      </div>
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {emails.map((em, i) => (
            <Badge key={i} variant="secondary" className={`bg-${color}-50 text-${color}-700 text-xs`}>
              <Mail className="w-3 h-3 mr-1" />{em}
              <button onClick={() => removeEmail(field, em)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-4 pt-4 border-t space-y-4">
      <EmailList label="To" field="recipient_emails" emails={config.recipient_emails || []} input={toInput} setInput={setToInput} />
      <EmailList label="CC" field="cc_emails" emails={config.cc_emails || []} input={ccInput} setInput={setCcInput} color="blue" />
      <EmailList label="BCC" field="bcc_emails" emails={config.bcc_emails || []} input={bccInput} setInput={setBccInput} color="gray" />

      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Subject Template</label>
        <Input className="mt-1 text-sm" value={config.subject_template || ""}
          onChange={e => updateField("subject_template", e.target.value)}
          placeholder="e.g. [Int] {{count}} Reminder: {{date}}"
        />
        <p className="text-xs text-gray-400 mt-1">Placeholders (vary by type): {"{{count}}"}, {"{{date}}"}, {"{{field}}"}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">From Name</label>
          <Input className="mt-1 text-sm" value={config.from_name || ""}
            onChange={e => updateField("from_name", e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Footer Text</label>
          <Input className="mt-1 text-sm" value={config.footer_text || ""}
            onChange={e => updateField("footer_text", e.target.value)}
          />
        </div>
      </div>

      {GREETING_TYPES.includes(config.action_type) && (
        <RecipientNames
          emails={GREETING_INCLUDES_CC.includes(config.action_type)
            ? [...(config.recipient_emails || []), ...(config.cc_emails || [])]
            : (config.recipient_emails || [])}
          names={config.recipient_names}
          onChange={v => updateField("recipient_names", v)} />
      )}

      {supportsSchedule && scheduleMode === "multi_daily" && (
        <ScheduleTimes times={config.schedule_times}
          onChange={v => updateField("schedule_times", v)} />
      )}

      {supportsDocumentToggles && (
        <DocumentToggles config={config} onChange={updateField} />
      )}

      {supportsSchedule && scheduleMode !== "multi_daily" && (
        <div className="space-y-3">
          {(config.schedule_frequency === "weekly" || config.schedule_day_of_week != null) && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Day of week (IST)</label>
              <Select value={String(config.schedule_day_of_week ?? 5)}
                onValueChange={v => updateField("schedule_day_of_week", parseInt(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DOW_OPTIONS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Hour (0-23, IST)</label>
              <Input type="number" min={0} max={23} className="mt-1 text-sm"
                value={config.schedule_hour ?? ""} onChange={e => updateField("schedule_hour", e.target.value ? parseInt(e.target.value) : null)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule Minute (0-59)</label>
              <Input type="number" min={0} max={59} className="mt-1 text-sm"
                value={config.schedule_minute ?? ""} onChange={e => updateField("schedule_minute", e.target.value ? parseInt(e.target.value) : null)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => onUpdate(config)} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminNotificationSetup;
